import * as React from "react";
import { useLayoutEffect, useRef } from "react";
import { Placement } from "@popperjs/core/lib/enums";
import flip from "@popperjs/core/lib/modifiers/flip";
import offset from "@popperjs/core/lib/modifiers/offset";
import preventOverflow from "@popperjs/core/lib/modifiers/preventOverflow";
import {
	defaultModifiers,
	Instance,
	Modifier,
	popperGenerator,
	VirtualElement,
} from "@popperjs/core/lib/popper-lite";
import { Point } from "./D3Drag";
import { useLayoutEffectSkipFirst } from "./Hooks";

// TODO: These "DANGER" could be removed, but I haven't seen the need for it yet.

export interface Props {
	/** See popper docs. */
	placement: Placement;
	/** Element to anchor to.  DANGER: Will not attach to new anchor after creation!  Remount PopperWrapper if you need this. */
	anchor: Element | VirtualElement;
	/** Popup content.  DANGER:  Will not attach to newly mounted children after creation!  Remount PopperWrapper if you need this. */
	children: React.ReactElement;
	/** See popper docs. */
	offset: [skidding: number, distance: number];
	/** Optional x-y offset applied after other calculations are made. */
	displacement?: Point;
	/** If true, the popup will not be automatically repositioned after creation. */
	oneShot?: boolean;
}

const displace: Modifier<"displace", Point> = {
	name: "displace",
	enabled: true,
	phase: "afterMain",
	fn: (args) => {
		const { popperOffsets } = args.state.modifiersData;
		if (popperOffsets) {
			popperOffsets.x += args.options.x || 0;
			popperOffsets.y += args.options.y || 0;
		}
	},
	options: {
		x: 0,
		y: 0,
	},
};

// const debug: Modifier<"debug", any> = {
// 	name: "debug",
// 	enabled: true,
// 	phase: "beforeMain",
// 	fn: args =>
// 		console.log(
// 			JSON.stringify(args, (k, v) => {
// 				if (!v) {
// 					return v;
// 				}
// 				if (v.constructor === Number || v.constructor === String || v.constructor === Boolean || v.constructor === Object) {
// 					return v;
// 				}
// 				return void 0;
// 			})
// 		),
// };

const createPopperFull = popperGenerator({
	defaultModifiers: [/*debug, */ ...defaultModifiers, offset, flip, displace, preventOverflow],
});

const createPopperOneShot = popperGenerator({
	defaultModifiers: [/*debug, */ ...defaultModifiers, offset, flip, displace, preventOverflow],
});

const oneShotDisableModifiers = [
	{
		name: "flip",
		enabled: false,
	},
	{ name: "preventOverflow", enabled: false },
];

/** Minimal wrapper around Popper */
export function PopperWrapper(props: Props) {
	const popper = useRef<Instance>(undefined);
	const target = useRef<HTMLElement | null>(null);

	const getOptions = () => ({
		placement: props.placement,
		modifiers: [
			{
				name: "offset",
				options: { offset: props.offset },
			},
			{
				name: "displace",
				options: props.displacement,
			},
			{
				// don't move the popup when the anchor scrolls
				name: "eventListeners",
				options: { scroll: false },
			},
		],
	});

	useLayoutEffect(() => {
		if (!props.oneShot) {
			popper.current = createPopperFull(props.anchor, target.current!, getOptions());
		} else {
			popper.current = createPopperOneShot(props.anchor, target.current!, {
				...getOptions(),
				onFirstUpdate: async () => {
					await popper.current?.setOptions({ modifiers: oneShotDisableModifiers });
					// This seems to be a bug in popper:
					// Disabling modifiers causes their "last calculated effect"
					// to be lost and for the popper to snap to a location as if they never existed.
					// But, subsequent updates will then use those "last calculated effects again".
					// We want this behavior, so we have to setOptions() one more time here to rerun the pipeline.
					popper.current?.setOptions(getOptions());
				},
			});
		}
		return () => {
			popper.current!.destroy();
			popper.current = undefined;
		};
	}, []);

	useLayoutEffectSkipFirst(() => {
		popper.current!.setOptions(getOptions());
	}, [props.placement, props.offset[0], props.offset[1], props.displacement]);

	return React.cloneElement(props.children, { ref: target } as any);
}
